/**
 * SignalPassArt — the little illustration on a signal pass.
 *
 * A signal isn't a real ticket, it's "we're all meeting up." So the art is a few
 * party stick figures walking toward each other under a signal beacon, with confetti.
 * Line-art only: strokes use `currentColor` so it reads on any background, with the
 * Signal cyan (#03FFFF) as the one accent (the beacon + a few sparks), matching the
 * species-mark token law. No external asset — inline SVG, crisp at any size, works
 * offline on a phone held up at a door.
 */
export default function SignalPassArt({ className = '' }) {
  const CYAN = '#03FFFF';
  return (
    <svg
      viewBox="0 0 400 240"
      className={className}
      role="img"
      aria-label="Party people meeting up under a Portal"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Signal beacon — the cyan mark, radiating. */}
      <g stroke={CYAN} strokeWidth="3" strokeLinecap="round">
        <circle cx="200" cy="40" r="7" fill={CYAN} stroke="none" />
        <path d="M181 40a19 19 0 0 1 38 0" />
        <path d="M170 40a30 30 0 0 1 60 0" opacity="0.6" />
        <path d="M159 40a41 41 0 0 1 82 0" opacity="0.3" />
      </g>

      {/* Ground line. */}
      <line x1="30" y1="200" x2="370" y2="200" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />

      {/* Three friends converging on the middle. Each: party hat, head, body, arms, legs. */}
      {/* Left figure, leaning in / walking right. */}
      <g stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="92,96 104,96 98,80" fill={CYAN} stroke="currentColor" strokeWidth="2.5" />
        <circle cx="98" cy="108" r="11" fill="none" />
        <line x1="98" y1="119" x2="98" y2="160" />
        {/* arms — one raised in a wave */}
        <line x1="98" y1="130" x2="78" y2="120" />
        <line x1="98" y1="130" x2="120" y2="138" />
        {/* legs mid-stride */}
        <line x1="98" y1="160" x2="86" y2="198" />
        <line x1="98" y1="160" x2="112" y2="198" />
      </g>

      {/* Middle figure, arms up celebrating. */}
      <g stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="194,86 206,86 200,68" fill="currentColor" stroke="currentColor" strokeWidth="2.5" />
        <circle cx="200" cy="98" r="12" fill="none" />
        <line x1="200" y1="110" x2="200" y2="158" />
        <line x1="200" y1="122" x2="180" y2="104" />
        <line x1="200" y1="122" x2="220" y2="104" />
        <line x1="200" y1="158" x2="188" y2="198" />
        <line x1="200" y1="158" x2="212" y2="198" />
      </g>

      {/* Right figure, leaning in / walking left. */}
      <g stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="296,96 308,96 302,80" fill={CYAN} stroke="currentColor" strokeWidth="2.5" />
        <circle cx="302" cy="108" r="11" fill="none" />
        <line x1="302" y1="119" x2="302" y2="160" />
        <line x1="302" y1="130" x2="322" y2="120" />
        <line x1="302" y1="130" x2="280" y2="138" />
        <line x1="302" y1="160" x2="314" y2="198" />
        <line x1="302" y1="160" x2="288" y2="198" />
      </g>

      {/* Confetti — a scatter of +, x and dots, cyan and current. */}
      <g strokeWidth="2.5" strokeLinecap="round">
        <path d="M50 70l0 12M44 76l12 0" stroke={CYAN} />
        <path d="M348 64l8 8M356 64l-8 8" stroke="currentColor" />
        <path d="M338 128l0 10M333 133l10 0" stroke="currentColor" opacity="0.7" />
        <path d="M62 150l7 7M69 150l-7 7" stroke={CYAN} />
        <circle cx="150" cy="58" r="2.5" fill="currentColor" stroke="none" />
        <circle cx="252" cy="150" r="2.5" fill={CYAN} stroke="none" />
        <circle cx="118" cy="74" r="2.5" fill={CYAN} stroke="none" />
        <path d="M256 60l0 10M251 65l10 0" stroke="currentColor" opacity="0.7" />
      </g>
    </svg>
  );
}
